import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { OneClick } from '@/components/blocks/OneClick';
import { PasswordInput } from '@/components/blocks/PasswordInput';
import { OTP_METHODS } from '@/data/Content/Login';
import { useLocalization } from '@/data/Localization';
import { REG_EX } from '@/utils/address';
import {
	Box,
	FormControl,
	Radio,
	RadioGroup,
	Stack,
	Typography,
	FormHelperText,
	type SxProps,
} from '@mui/material';
import { Fragment, useContext, useRef } from 'react';
import { EventsContext } from '@/data/context/events';
import { useLoginOrigin } from '@/data/context/loginOrigin';
import { useSettings } from '@/data/Settings';

type RadioOptionProps = {
	value: string;
	password?: string;
	passwordError?: string;
	checked: boolean;
	title: string;
	detail: string;
	detailSx?: SxProps;
	index: number;
	disabled?: boolean;
	submitButtonRef?: React.Ref<HTMLDivElement>;
	onChangePassword?: (password: string) => void;
	onSubmitOtpMethod?: () => void;
	onPasswordFocus?: () => void;
	onClick: () => void;
};

const RadioOption: React.FC<RadioOptionProps> = ({
	value,
	checked,
	title,
	detail,
	detailSx,
	index,
	password,
	passwordError,
	onChangePassword,
	onClick,
	onSubmitOtpMethod,
	onPasswordFocus,
	disabled,
	submitButtonRef,
}) => {
	const RouteLocal = useLocalization('Routes');
	const { onPasswordRecoveryBegin } = useContext(EventsContext);
	const login_origin = useLoginOrigin();
	const { settings } = useSettings();

	const handleClickForgotPassword = () => {
		onPasswordRecoveryBegin({ gtm: { settings, login_origin } });
	};

	const handleStopPropagation = (e: any) => {
		e?.stopPropagation();
		e?.preventDefault();
	};

	const handlePasswordFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.stopPropagation();
		onPasswordFocus?.();
	};
	const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		e.preventDefault();
		onChangePassword?.(e.target.value);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onSubmitOtpMethod?.();
		}
	};

	return (
		<Box
			sx={(theme) => ({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				p: 1.5,
				borderTop: index === 0 ? `1px solid ${theme.palette.divider}` : 'none',
				borderLeft: `1px solid ${theme.palette.divider}`,
				borderRight: `1px solid ${theme.palette.divider}`,
				borderBottom: `1px solid ${theme.palette.divider}`,
				borderRadius: index === 0 ? '8px 8px 0 0' : index === 3 ? '0 0 8px 8px' : '0',
				cursor: 'pointer',
				transition: 'border-color 0.2s, background-color 0.2s',
				backgroundColor: 'white',
			})}
			onClick={onClick}
		>
			<Box
				sx={{
					width: '100%',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-start',
					alignItems: 'center',
				}}
			>
				<Radio value={value} checked={checked} />

				<Box>
					<Typography
						sx={{
							fontWeight: checked ? '800' : '500',
							fontSize: '15px',
						}}
					>
						{title}
					</Typography>
					<Typography
						sx={{
							fontWeight: checked ? '800' : '500',
							fontSize: '15px',
							...detailSx,
						}}
					>
						{detail}
					</Typography>
				</Box>
			</Box>
			{value === OTP_METHODS.PASSWORD && checked ? (
				<Fragment>
					<PasswordInput
						sx={{
							...addressFormFieldsSX,
							mt: '13px',
							'& .MuiOutlinedInput-input': { fontSize: '16px' },
						}}
						required
						name="logonPassword"
						label="Contraseña"
						value={password}
						inputProps={{
							maxLength: 40,
							pattern: REG_EX.PASSWORD.source,
						}}
						error={!!passwordError}
						helperText={passwordError}
						onChange={handleChangePassword}
						onBlur={handleStopPropagation}
						onFocus={handlePasswordFocus}
						onClick={handleStopPropagation}
						onKeyDown={handleKeyDown}
					/>
					<DpButton
						sx={{ mt: '11px' }}
						variant="text"
						href={`/${RouteLocal.ForgotPassword.route.t()}`}
						onClick={handleClickForgotPassword}
					>
						{'Olvidé mi contraseña'}
					</DpButton>
				</Fragment>
			) : null}
			{checked ? (
				<Box
					ref={submitButtonRef}
					onClick={handleStopPropagation}
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'center',
						scrollMarginBottom: '24px',
					}}
				>
					<OneClick
						wrapper="dp-button"
						sx={{ mt: '20px', width: '100%', maxWidth: '400px', alignSelf: 'center' }}
						id="button-select-otp-method-submit"
						disabled={disabled}
						onClick={onSubmitOtpMethod}
					>
						{'Continuar'}
					</OneClick>
				</Box>
			) : null}
		</Box>
	);
};

type Props = {
	otpMethod?: OTP_METHODS;
	otpMethodError?: string;
	disabled?: boolean;
	maskedPhone?: string | null;
	maskedEmail?: string | null;
	password: string;
	passwordError?: string;
	onChangePassword: (password: string) => void;
	onChangeOtpMethod: (otpMethod: OTP_METHODS) => void;
	onSubmitOtpMethod: () => void;
	onToggleLoginPassword: () => void;
};

export const SelectOtpMethodStage: React.FC<Props> = ({
	otpMethod,
	otpMethodError,
	disabled,
	maskedPhone,
	maskedEmail,
	password,
	passwordError,
	onChangePassword,
	onChangeOtpMethod,
	onSubmitOtpMethod,
}) => {
	const submitButtonRef = useRef<HTMLDivElement>(null);

	const handlePasswordFocus = () => {
		setTimeout(() => {
			const button = submitButtonRef.current;
			if (!button) return;
			const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
			const buttonBottom = button.getBoundingClientRect().bottom;
			const overflow = buttonBottom - viewportHeight + 16;
			if (overflow > 0) {
				window.scrollBy({ top: overflow, behavior: 'smooth' });
			}
		}, 350);
	};

	return (
		<Stack width="100%">
			<FormControl fullWidth error={!!otpMethodError}>
				<RadioGroup
					value={otpMethod}
					onChange={(e) => onChangeOtpMethod(e.target.value as OTP_METHODS)}
				>
					<RadioOption
						value={OTP_METHODS.PASSWORD}
						checked={otpMethod === OTP_METHODS.PASSWORD}
						title="Iniciar sesión con contraseña"
						detail="Si no la recuerdas, puedes recuperarla"
						detailSx={{ fontSize: 'var(--font-size-small)', fontWeight: 300 }}
						index={0}
						password={password}
						passwordError={passwordError}
						disabled={disabled}
						submitButtonRef={submitButtonRef}
						onChangePassword={onChangePassword}
						onSubmitOtpMethod={onSubmitOtpMethod}
						onPasswordFocus={handlePasswordFocus}
						onClick={() => onChangeOtpMethod(OTP_METHODS.PASSWORD)}
					/>
					<RadioOption
						value={OTP_METHODS.EMAIL}
						checked={otpMethod === OTP_METHODS.EMAIL}
						title="Enviarme el código al correo electrónico"
						detail={maskedEmail || ''}
						index={1}
						disabled={disabled}
						submitButtonRef={submitButtonRef}
						onSubmitOtpMethod={onSubmitOtpMethod}
						onClick={() => onChangeOtpMethod(OTP_METHODS.EMAIL)}
					/>
					<RadioOption
						value={OTP_METHODS.SMS}
						checked={otpMethod === OTP_METHODS.SMS}
						title="Enviarme el código por mensaje de texto"
						detail={maskedPhone || ''}
						index={2}
						disabled={disabled}
						submitButtonRef={submitButtonRef}
						onSubmitOtpMethod={onSubmitOtpMethod}
						onClick={() => onChangeOtpMethod(OTP_METHODS.SMS)}
					/>
				</RadioGroup>

				{otpMethodError ? (
					<FormHelperText
						error
						sx={(theme) => ({
							color: '#d32f2f',
							fontSize: 'var(--font-size-caption)',
							margin: 0,
							padding: 0,
							marginTop: theme.spacing(0.5),
						})}
					>
						{otpMethodError}
					</FormHelperText>
				) : null}
			</FormControl>
		</Stack>
	);
};
